import React, { Fragment, useState } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  GQLJobState,
  GQLMerchantDeliveryType,
  GQLParty,
  GQLPlatformMerchantOrder
} from '@jewelutions/core-crm-server-api-client-domain';

import { JwlTable, LoadingSpinner, ErrorComponent } from '../../../../components';
import { GQLOperationBuilder } from '../../../../operations/GQLOperationBuilder';
import { MerchantNavigation } from '../../navigation/MerchantNavigation';
import { GQLAddressHelper } from '../../../../operations/helpers/GQLAddressHelper';
import { Typography, Button, Card, Grid } from '@material-ui/core';
import { Play } from 'react-feather';
import { toast } from 'react-toastify';
import { successNotification } from '../../../../core/utils';

interface IProps {
  party: GQLParty;
}

const PlatformProducerOrdersList: React.FC<IProps> = ({ party }: IProps) => {
  const history = useHistory();
  const merchantChannelId = MerchantNavigation.platformMerchantOrderList.paramsFromPath(useRouteMatch())
    .merchantChannelId;
  const [errorComponentState, setErrorComponentState] = useState(false);
  const [filteredOrders, setFilteredOrders] = useState('');
  const { t: translate } = useTranslation();

  const [retrieveMerchantOrdersFromWebshop] = GQLOperationBuilder.webshop.retrieveMerchantOrdersFromWebshop();

  const {
    data: ordersData,
    loading: ordersLoading,
    refetch: refetchOrders
  } = GQLOperationBuilder.merchant.getPlatformMerchantOrders({
    merchantPartyId: party.id,
    merchantChannelId: merchantChannelId
  });

  const {
    data: ordersJobData,
    loading: ordersJobLoading,
    refetch: refetchOrdersJob
  } = GQLOperationBuilder.webshop.retrieveMerchantOrdersFromWebshopJobs({
    merchantChannelId: merchantChannelId
  });

  const reloadData = async () => {
    refetchOrders();
    refetchOrdersJob();
  };

  if (ordersLoading || ordersJobLoading) return <LoadingSpinner />;
  const ordersJob =
    GQLOperationBuilder.webshop.jobSummaryArrayFromResponse(ordersJobData).length === 0
      ? undefined
      : GQLOperationBuilder.webshop.jobSummaryArrayFromResponse(ordersJobData)[0];

  const handleFilterChange = (event: any) => setFilteredOrders(event.target.value);

  const getOrderName = (order: GQLPlatformMerchantOrder) =>
    order.merchantDelivery.deliveryType === GQLMerchantDeliveryType.Warehouse
      ? order.merchantDelivery.warehouseDelivery!.companyName
      : `${order.merchantDelivery.dropshipDelivery!.personFirstName} ${
          order.merchantDelivery.dropshipDelivery!.personLastName
        }`;

  const getOrderLocation = (order: GQLPlatformMerchantOrder) =>
    order.merchantDelivery.deliveryType === GQLMerchantDeliveryType.Warehouse
      ? GQLAddressHelper.addressToString(order.merchantDelivery.warehouseDelivery!.address!)
      : GQLAddressHelper.addressToString(order.merchantDelivery.dropshipDelivery!.address!);

  const getOrderRows = () => {
    return GQLOperationBuilder.merchant
      .platformMerchantOrdersArrayFromResponse(ordersData)
      .filter(order => {
        return (
          getOrderName(order)
            .toLowerCase()
            .includes(filteredOrders.toLowerCase().trim()) ||
          getOrderLocation(order)
            .toLowerCase()
            .includes(filteredOrders.toLowerCase().trim())
        );
      })
      .map(order => {
        return {
          id: order.id,
          cells: [
            order.id,
            getOrderName(order),
            getOrderLocation(order),
            order.state,
            new Date(Date.parse(order.createdAt)).toLocaleString(),
            new Date(Date.parse(order.updatedAt)).toLocaleString()
          ]
        };
      });
  };

  const navigateToOrderDetails = (orderId: string) => {
    history.push(
      MerchantNavigation.platformMerchantOrderDetails.pathFromParams({
        merchantChannelId: merchantChannelId,
        orderId: orderId
      })
    );
  };

  const forceRetrieveMerchantOrdersFromWebshop = () => {
    const mutationVariables = { merchantChannelId: merchantChannelId };
    retrieveMerchantOrdersFromWebshop({ variables: mutationVariables }).then(_result => {
      successNotification(toast, 'New orders sync scheduled', reloadData());
    });
  };

  return (
    <Fragment>
      <Grid container direction="row">
        <Grid item md={4} style={{ marginBottom: '8px' }}>
          <Card style={{ padding: '8px' }}>
            {ordersJob === undefined && <Typography>Orders were never retrievd from the shop.</Typography>}
            {ordersJob !== undefined && (
              <Typography>
                Orders were last scheduled for retrieval from the shop at {ordersJob.createdAt}. The
                synchronisation task{' '}
                {[GQLJobState.Created, GQLJobState.Started].includes(ordersJob.state) ? 'is in progress' : ''}
                {[GQLJobState.FinishedSuccess].includes(ordersJob.state) ? 'has finished with success' : ''}
                {[GQLJobState.FinishedFailed].includes(ordersJob.state)
                  ? 'encouuntered some issues and could not finish properly'
                  : ''}
              </Typography>
            )}
            <Typography>
              The orders are automaticlly & periodically synced every couple of minutes. Press "Synchronise
              now" if you want to schedule a sync again right now.
            </Typography>
            <Button
              disabled={
                ordersJob && [GQLJobState.Started, GQLJobState.Created].includes(ordersJob.state)
                  ? true
                  : false
              }
              variant="contained"
              color="primary"
              style={{ width: '50%' }}
              onClick={() => {
                forceRetrieveMerchantOrdersFromWebshop();
              }}
            >
              <Play />
              <span style={{ marginLeft: '4px' }}>Synchronise now</span>
            </Button>
          </Card>
        </Grid>
      </Grid>

      <JwlTable
        title="Shop orders list"
        description={`The list of all current orders retrieved from the shop of ${party.name}.`}
        columns={['ID', 'Name', 'Delivery location', 'State', 'Created At', 'Updated At']}
        rows={getOrderRows()}
        onFilterChange={handleFilterChange}
        onTableRowClick={navigateToOrderDetails}
      />
    </Fragment>
  );
};

export default PlatformProducerOrdersList;
